import { AnimationEvent } from '@angular/animations';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Directive,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  inject,
  Input,
  OnInit,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subject } from 'rxjs';

import { DrawerMode } from '@treetop/shared/model';

import { ttDrawerAnimations } from './drawer-animations';
import { DrawerService } from './drawer.service';
import { IconDirective } from '../icon/icon.directive';

@Component({
  selector: 'tt-drawer',
  standalone: true,
  imports: [MatTooltipModule, IconDirective, MatIconModule],
  templateUrl: './drawer.component.html',
  styleUrl: './drawer.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  animations: [ttDrawerAnimations.transformDrawer],
})
export class DrawerComponent implements OnInit {
  private elementRef = inject(ElementRef);
  private changeDetectorRef = inject(ChangeDetectorRef);

  @HostBinding('class.tt-drawer') public hostCls = true;

  @Input()
  public set opened(value: boolean) {
    this.setOpen(value);
  }
  public get opened(): boolean {
    return this._opened;
  }

  private _minWidth = 400;
  @Input() public set minWidth(minWidth: number) {
    this._minWidth = minWidth;
    this.elementRef.nativeElement.style.minWidth = `${minWidth}px`;
  }

  @Input() public showToggle = false;

  public readonly _animationStarted = new Subject<AnimationEvent>();

  public readonly _animationEnd = new Subject<AnimationEvent>();

  @Output() public toggleButtonClicked = new EventEmitter<void>();

  @HostBinding('class.tt-drawer-opened')
  public _opened = undefined;

  @HostBinding('class.tt-drawer-show-toggle')
  public get showToggleClass(): boolean {
    return this.showToggle;
  }

  @HostBinding('@transform')
  public _animationState:
    | 'open'
    | 'void'
    | 'defaultOpen'
    | 'defaultClose'
    | 'openToggle'
    | 'voidToggle'
    | 'defaultOpenToggle'
    | 'defaultCloseToggle' = undefined;

  @HostListener('@transform.start', ['$event'])
  public onAnimationStart(event: AnimationEvent) {
    this._animationStarted.next(event);
  }

  @HostListener('@transform.done', ['$event'])
  public onAnimationEnd(event: AnimationEvent) {
    this._animationEnd.next(event);
  }

  public ngOnInit(): void {
    this.elementRef.nativeElement.style.minWidth = `${this._minWidth}px`;
  }

  private initialToggle = true;

  public getWidth(): number {
    if (!this._opened) {
      return 0;
    }

    return this.elementRef.nativeElement ? this.elementRef.nativeElement.offsetWidth || 0 : 0;
  }

  public setWidth(width: number): void {
    if (width < this._minWidth) {
      width = this._minWidth;
    }

    this.elementRef.nativeElement.style.width = `${width}px`;
    this.changeDetectorRef.markForCheck();
  }

  public setOpen(isOpen: boolean) {
    this.setOpenAnimations(isOpen, this.showToggle);
  }

  private setOpenAnimations(isOpen: boolean, withToggle: boolean) {
    this._opened = isOpen;

    const toggleSuffix = withToggle ? 'Toggle' : '';

    if (this.initialToggle && this._opened) {
      this._animationState = `open${toggleSuffix}`;
      this.initialToggle = false;
      return;
    }

    if (this.initialToggle && !this._opened) {
      this._animationState = `void${toggleSuffix}`;
      this.initialToggle = false;
      return;
    }

    if (this._opened) {
      this._animationState = `open${toggleSuffix}`;
      this.initialToggle = false;
      return;
    }

    this._animationState = `void${toggleSuffix}`;
    this.initialToggle = false;
  }
}

@Directive({
  selector: '[ttDrawerLeft]',
  standalone: true,
})
export class DrawerLeftDirective {
  private drawerService = inject(DrawerService);

  @HostBinding('class.tt-drawer-left') public hostCls = true;
  @HostBinding('class.tt-drawer-left--over') public get isOver() {
    return this.drawerService.leftDrawerMode() === 'over';
  }

  @Input()
  public set resizable(value: boolean) {
    this.drawerService.isLeftResizable.set(value);
  }

  @Input()
  public set opened(value: boolean) {
    this.drawerService.isLeftOpen.set(value);
  }

  @Input() public set mode(mode: DrawerMode) {
    this.drawerService.leftDrawerMode.set(mode);
  }
}

@Directive({
  selector: '[ttDrawerRight]',
  standalone: true,
})
export class DrawerRightDirective {
  private drawerService = inject(DrawerService);

  @HostBinding('class.tt-drawer-right') public hostCls = true;
  @HostBinding('class.tt-drawer-right--over') public get isOver() {
    return this.drawerService.rightDrawerMode() === 'over';
  }

  @Input()
  public set resizable(value: boolean) {
    this.drawerService.isRightResizable.set(value ?? true);
  }

  @Input()
  public set opened(value: boolean) {
    this.drawerService.isRightOpen.set(value);
  }

  @Input() public set mode(mode: DrawerMode) {
    this.drawerService.rightDrawerMode.set(mode);
  }
}
